import { useState } from 'react';
import { format } from 'date-fns';
import Panel from '../../Panel';
import RejectChangeRequestModal from '../modals/rejectChangeRequest';
import ApproveChangeRequestModal from '../modals/approveChangeRequest';
import { Link } from 'react-router-dom';
import { VendorChangeRequest } from '~/types/fundrequests';

interface TitlePanelProps {
    vendorChangeRequest: VendorChangeRequest;
    isProcessing: boolean;
    onCancel: () => void;
    onReject: (reason: string) => void;
    onApprove: () => void;
    isRejected: boolean;
    isApproved: boolean;
    changeCount?: number;
    mipConfirmed?: boolean;
    setMipConfirmed?: React.Dispatch<React.SetStateAction<boolean>>;
}

const TitlePanel: React.FC<TitlePanelProps> = ({
    vendorChangeRequest,
    isProcessing,
    onCancel,
    onReject,
    onApprove,
    isRejected,
    isApproved,
    changeCount,
    mipConfirmed,
    setMipConfirmed,
}) => {
    const [rejectModalOpen, setRejectModalOpen] = useState(false);
    const [approveModalOpen, setApproveModalOpen] = useState(false);

    const isPending = !isRejected && !isApproved;
    const formattedDate = vendorChangeRequest.created_date
        ? format(new Date(vendorChangeRequest.created_date), 'MMM dd, yyyy')
        : '';

    const handleRejectConfirm = (reason: string) => {
        onReject(reason);
    };

    return (
        <div className='mb-7.5'>
            <Panel>
                <div className='xl:flex xl:justify-between'>
                    <div className='mb-6 flex gap-[16px] xl:mb-0'>
                        <div>
                            <div className='flex items-center'>
                                <h1 className='mr-[16px] text-[38px]/[44px] font-medium'>
                                    {vendorChangeRequest.vendor?.name}
                                </h1>
                            </div>
                            <div className='flex items-center gap-[16px]'>
                                {isPending && (
                                    <div className='flex items-center'>
                                        <span className='inline-flex rounded-full bg-[#952E46] px-2 py-1 text-sm text-white'>
                                            Needs Review
                                        </span>
                                    </div>
                                )}

                                {isApproved && (
                                    <div className='flex items-center'>
                                        <span className='inline-flex rounded-full bg-[#AFC12D] px-2 py-1 text-sm'>
                                            Approved
                                        </span>
                                    </div>
                                )}

                                {isRejected && (
                                    <div className='flex items-center'>
                                        <span className='inline-flex rounded-full bg-danger px-2 py-1 text-sm text-white'>
                                            Rejected
                                        </span>
                                    </div>
                                )}
                                <div className='text-[16px]/[24px] text-[#949599]'>
                                    Vendor ID:{' '}
                                    {vendorChangeRequest.vendor?.vendor_id}
                                </div>
                                <div className='text-[16px]/[24px] text-[#949599]'>
                                    Requested: {formattedDate}
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className='xl:flex xl:items-center'>
                        <Link
                            to={`/payees/request-changes/`}
                            className='m-auto mr-3 py-[7px] text-[14px]/[18px] font-bold uppercase text-primary hover:bg-opacity-90'
                        >
                            Back to Change Requests
                        </Link>

                        {isPending && (
                            <>
                                <button
                                    className='m-auto mr-3 rounded-full border-[2px] border-danger px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase text-danger transition-colors duration-200 hover:border-[#e6f7f8] hover:bg-[#e6f7f8]'
                                    onClick={() => {
                                        setRejectModalOpen(true);
                                    }}
                                    disabled={isProcessing}
                                >
                                    Reject
                                </button>
                                <button
                                    className={`m-auto mr-3 rounded-full border-[2px] border-warning bg-warning px-[24px] py-[7px] text-[14px]/[18px] font-bold uppercase transition-colors duration-500 hover:border-[#e6f7f8] hover:bg-[#e6f7f8]`}
                                    onClick={() => {
                                        setApproveModalOpen(true);
                                    }}
                                    disabled={isProcessing}
                                >
                                    Approve{' '}
                                    {changeCount
                                        ? `${changeCount} Changes`
                                        : 'Changes'}
                                </button>
                            </>
                        )}
                    </div>
                </div>
            </Panel>
            <RejectChangeRequestModal
                modalOpen={rejectModalOpen}
                setModalOpen={setRejectModalOpen}
                onConfirm={handleRejectConfirm}
                hasAchDocument={!!vendorChangeRequest.ach_document_url}
            />
            <ApproveChangeRequestModal
                modalOpen={approveModalOpen}
                setModalOpen={setApproveModalOpen}
                onConfirm={onApprove}
                mipConfirmed={mipConfirmed}
                setMipConfirmed={setMipConfirmed}
                hasAchDocument={!!vendorChangeRequest.ach_document_url}
            />
        </div>
    );
};

export default TitlePanel;
